import getPcsCount from '@components/ncFileVersions/helpers/getPcsCount';
import { NCFileVersion } from '@interfaces/search';
import { MachineEventCycleStatisticsResponse } from '@api/domain/types';

// Pcs produced per version, keyed by the version's sequenceNumber.
export type NCFileVersionPcsCounts = Record<number, number>;

type NCFileVersionCycles = MachineEventCycleStatisticsResponse['cycles'];

// pcsCount/pcs are computed client-side and not part of the generated version DTO.
type NCFileVersionWithPcs = NCFileVersion & { pcs?: number | null; pcsCount?: number | null };

// Seed every version's sequence number at 0 so versions with no cycles still report "0 Pcs".
export const createInitialVersionPcsCounts = (versions: NCFileVersion[] | null | undefined): NCFileVersionPcsCounts => {
  const counts: NCFileVersionPcsCounts = {};

  versions?.forEach((version) => {
    if (typeof version.sequenceNumber === 'number') {
      counts[version.sequenceNumber] = 0;
    }
  });

  return counts;
};

// Add each cycle's quantityDelta to every version sequence number it ran under.
export const addCyclePcsCounts = (counts: NCFileVersionPcsCounts, cycles: NCFileVersionCycles | null | undefined): NCFileVersionPcsCounts => {
  const nextCounts = { ...counts };

  cycles?.forEach((cycle) => {
    const parsedDelta = Number(cycle.quantityDelta);
    const quantityDelta = Number.isFinite(parsedDelta) ? parsedDelta : 0;

    cycle.ncFileVersionSequenceNumbers?.forEach((sequenceNumber) => {
      if (nextCounts[sequenceNumber] !== undefined) {
        nextCounts[sequenceNumber] += quantityDelta;
      }
    });
  });

  return nextCounts;
};

// Write the resolved pcs count onto each version, preferring fetched counts and falling back to any existing value.
export const mergePcsCountsIntoVersions = (versions: NCFileVersionWithPcs[] | null | undefined, counts: NCFileVersionPcsCounts): NCFileVersion[] => {
  if (!versions?.length) {
    return [];
  }

  return versions.map((version) => {
    const fetchedCount = typeof version.sequenceNumber === 'number' ? counts[version.sequenceNumber] : undefined;
    const nextCount = fetchedCount ?? getPcsCount(version);

    if (typeof nextCount !== 'number' || version.pcsCount === nextCount) {
      return version;
    }

    return { ...version, pcsCount: nextCount };
  });
};

export const arePcsCountsEqual = (a: NCFileVersionPcsCounts, b: NCFileVersionPcsCounts): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  return aKeys.length === bKeys.length && bKeys.every((key) => a[Number(key)] === b[Number(key)]);
};
